/**
 * API Keys utility functions.
 */

export type KeyStatus = "active" | "disabled" | "expired";

export function getApiKeyStatus(key: { enabled: boolean; expiresAt: string | Date | null }): KeyStatus {
  if (!key.enabled) return "disabled";
  if (!key.expiresAt) return "active";
  const expiry = typeof key.expiresAt === "string" ? new Date(key.expiresAt) : key.expiresAt;
  if (expiry.getTime() < Date.now()) return "expired";
  return "active";
}

export function formatKeyDateTime(dateStr: string | Date | null): string {
  if (!dateStr) return "Never";
  try {
    const d = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(dateStr);
  }
}

export function formatKeyRelativeTime(dateStr: string | Date | null): string {
  if (!dateStr) return "Never";
  const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatKeyDateTime(dateStr);
}

export function isKeyExpiringSoon(expiresAt: string | Date | null): boolean {
  if (!expiresAt) return false;
  const expiry = typeof expiresAt === "string" ? new Date(expiresAt) : expiresAt;
  const now = new Date();
  const daysUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
}

export function getApiKeyStatusConfig(status: KeyStatus) {
  switch (status) {
    case "active":
      return { label: "Active", color: "text-green-500", bgColor: "bg-green-100 dark:bg-green-900/30" };
    case "expired":
      return { label: "Expired", color: "text-orange-500", bgColor: "bg-orange-100 dark:bg-orange-900/30" };
    case "disabled":
      return { label: "Revoked", color: "text-red-500", bgColor: "bg-red-100 dark:bg-red-900/30" };
  }
}
